import { PostMessageAPI, PostMessageSendEventType, StateService } from 'flux-core';

export type CommentPostMessageAction = 'created' | 'updated' | 'resolved' | 'deleted';

interface CommentPostMessageData {
    id: string;
    parentId?: string;
    userId?: string;
    resolvedBy?: string;
    added?: number;
    edited?: number;
    resolved?: number;
    deleted?: number;
    lastUpdated?: number;
}

export const COMMENT_POSTMESSAGE_STATE = 'EmbeddedCommentPostMessageEnabled';

/**
 * Emits embedded comment lifecycle postMessages.
 * Events are emitted only from the actor session to avoid duplicates across collaborators.
 */
export function emitCommentActionPostMessage(
    state: StateService<any, any>,
    postMessage: PostMessageAPI,
    resourceId: string,
    comment: CommentPostMessageData,
    action: CommentPostMessageAction,
): void {
    if ( !comment || !comment.id || !resourceId ) {
        return;
    }

    const actorUserId = action === 'resolved' ? comment.resolvedBy : comment.userId;
    const currentUser = state.get( 'CurrentUser' );

    if (
        !state.get( 'ApplicationIsEmbedded' ) ||
        !state.get( 'EmbeddedAppAuthenticated' ) ||
        !state.get( COMMENT_POSTMESSAGE_STATE ) ||
        !actorUserId ||
        !currentUser ||
        actorUserId !== currentUser
    ) {
        return;
    }

    const eventType = getPostMessageEventType( action );
    postMessage.sendToParent( eventType, {
        diagramId: resourceId,
        commentId: comment.id,
        threadId: comment.parentId || comment.id,
        parentId: comment.parentId,
        userId: actorUserId,
        eventTime: getEventTime( comment, action ),
        action,
    });
}

function getPostMessageEventType( action: CommentPostMessageAction ): PostMessageSendEventType {
    switch ( action ) {
        case 'created':
            return PostMessageSendEventType.commentCreated;
        case 'updated':
            return PostMessageSendEventType.commentUpdated;
        case 'resolved':
            return PostMessageSendEventType.commentResolved;
        case 'deleted':
            return PostMessageSendEventType.commentDeleted;
        default:
            return PostMessageSendEventType.commentUpdated;
    }
}

function getEventTime( comment: CommentPostMessageData, action: CommentPostMessageAction ): number {
    switch ( action ) {
        case 'created':
            return comment.added || comment.lastUpdated || Date.now();
        case 'updated':
            return comment.edited || comment.lastUpdated || Date.now();
        case 'resolved':
            return comment.resolved || comment.lastUpdated || Date.now();
        case 'deleted':
            return comment.deleted || comment.lastUpdated || Date.now();
        default:
            return comment.lastUpdated || Date.now();
    }
}
